import { useCallback, useEffect, useState } from "react";
import type React from "react";
import {
  ActionIcon,
  Box,
  Center,
  Drawer,
  Group,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import { Badge, Button } from "@xplatform/design-system";
import { IconArrowBackUp, IconRefresh } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { mappingApi } from "@services/mappingApi";
import { ApiError } from "@utils/httpClient";
import type { RevisionViewModel } from "@models/revision";
import type { MappingResultRow } from "@models/mappingResult";
import { useErrorToast } from "@hooks/useErrorToast";

const REVISION_PAGE_SIZE = 50;

const formatTimestamp = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const SOURCE_COLOR: Record<RevisionViewModel["source"], string> = {
  patch: "blue",
  restore: "violet",
};

interface RevisionHistoryDrawerProps {
  opened: boolean;
  jobId: string;
  rowIndex: number;
  /** Called with the restored row so the parent can merge it locally
   * (no full result re-fetch). */
  onRestored: (row: MappingResultRow) => void;
  onClose: () => void;
}

const RevisionHistoryDrawer: React.FC<RevisionHistoryDrawerProps> = ({
  opened,
  jobId,
  rowIndex,
  onRestored,
  onClose,
}) => {
  const { t } = useTranslation();
  const [revisions, setRevisions] = useState<RevisionViewModel[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  useErrorToast(error, t("revisionHistory.title"), clearError);

  const fetchPage = useCallback(
    async (nextCursor: string | null): Promise<void> => {
      const isFirstPage = nextCursor === null;
      if (isFirstPage) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      try {
        const response = await mappingApi.listRevisions(jobId, rowIndex, {
          cursor: nextCursor ?? undefined,
          limit: REVISION_PAGE_SIZE,
        });
        setRevisions((prev) =>
          isFirstPage ? response.revisions : [...prev, ...response.revisions],
        );
        setCursor(response.cursor);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed to load history";
        setError(message);
      } finally {
        if (isFirstPage) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [jobId, rowIndex],
  );

  useEffect(() => {
    if (!opened) return;
    setRevisions([]);
    setCursor(null);
    setError(null);
    void fetchPage(null);
  }, [opened, fetchPage]);

  const handleRestore = useCallback(
    async (revisionId: string): Promise<void> => {
      setRestoringId(revisionId);
      try {
        const result = await mappingApi.restoreRevision(
          jobId,
          rowIndex,
          revisionId,
        );
        onRestored(result.row);
        // Re-fetch the revision list so the "restore" revision the server
        // just appended shows up at the top (this is the drawer's own list,
        // not the result rows).
        await fetchPage(null);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Restore failed";
        setError(message);
      } finally {
        setRestoringId(null);
      }
    },
    [fetchPage, jobId, onRestored, rowIndex],
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title={
        <Stack gap={0}>
          <Text fw={600}>{t("revisionHistory.title")}</Text>
          <Text size="xs" c="dimmed">
            {t("revisionHistory.rowLabel", {
              row: rowIndex,
              defaultValue: `Row #${rowIndex}`,
            })}
          </Text>
        </Stack>
      }
    >
      <Stack gap="sm">
        <Group justify="flex-end">
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => void fetchPage(null)}
            disabled={loading}
            aria-label={t("revisionHistory.refresh")}
          >
            <IconRefresh size={14} />
          </ActionIcon>
        </Group>

        {loading && revisions.length === 0 && (
          <Center mih={120}>
            <Loader size="sm" />
          </Center>
        )}

        {!loading && revisions.length === 0 && !error && (
          <Text size="sm" c="dimmed">
            {t("revisionHistory.empty")}
          </Text>
        )}

        {revisions.map((revision, idx) => {
          const isCurrent = idx === 0;
          const restoring = restoringId === revision.revisionId;
          return (
            <Box
              key={revision.revisionId}
              className="revision-history__item"
              p="sm"
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: 8,
              }}
            >
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap="xs">
                    <Badge size="xs" color={SOURCE_COLOR[revision.source]}>
                      {t(`revisionHistory.source.${revision.source}`, {
                        defaultValue: revision.source,
                      })}
                    </Badge>
                    <Badge size="xs" variant="light" color="gray">
                      {t(`resultRow.status.${revision.reviewStatus}`, {
                        defaultValue: revision.reviewStatus,
                      })}
                    </Badge>
                    {isCurrent && (
                      <Badge size="xs" variant="outline" color="teal">
                        {t("revisionHistory.current")}
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">
                    {formatTimestamp(revision.editedAt)}
                  </Text>
                  {revision.editedBy?.name && (
                    <Text size="xs">{revision.editedBy.name}</Text>
                  )}
                  {revision.chosenRank != null && (
                    <Text size="xs" c="dimmed">
                      {t("revisionHistory.pickedRank", {
                        rank: revision.chosenRank,
                        defaultValue: `Picked rank #${revision.chosenRank}`,
                      })}
                    </Text>
                  )}
                  {revision.reason && (
                    <Text size="xs" fs="italic" c="dimmed" lineClamp={2}>
                      {revision.reason}
                    </Text>
                  )}
                </Stack>
                {!isCurrent && (
                  <Button
                    size="compact-xs"
                    variant="light"
                    leftSection={<IconArrowBackUp size={12} />}
                    loading={restoring}
                    disabled={restoringId !== null && !restoring}
                    onClick={() => void handleRestore(revision.revisionId)}
                  >
                    {t("revisionHistory.restore")}
                  </Button>
                )}
              </Group>
            </Box>
          );
        })}

        {cursor !== null && (
          <Group justify="center">
            <Button
              variant="subtle"
              size="xs"
              loading={loadingMore}
              onClick={() => void fetchPage(cursor)}
            >
              {t("revisionHistory.loadMore")}
            </Button>
          </Group>
        )}
      </Stack>
    </Drawer>
  );
};

export default RevisionHistoryDrawer;
